import React, { useState } from 'react';
import { Calculator, Home, TrendingUp, ArrowRight, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import API_BASE_URL from '../config/api';

const MiniSimulateurHypotheque = () => {
  const [prixBien, setPrixBien] = useState(800000);
  const [fondsPropresPct, setFondsPropresPct] = useState(20);
  const [revenuAnnuel, setRevenuAnnuel] = useState(120000);
  const [tauxInteret, setTauxInteret] = useState(2.5);
  const [result, setResult] = useState(null);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const calculateCapacite = () => {
    // Calcul des fonds propres
    const fondsPropresMontant = prixBien * (fondsPropresPct / 100);
    const montantHypotheque = prixBien - fondsPropresMontant;
    
    // Calcul du taux d'effort (règle : max 33% du revenu brut)
    const chargeAnnuelle = montantHypotheque * (tauxInteret / 100);
    const chargeAmortissement = montantHypotheque * 0.01; // 1% amortissement
    const chargeEntretien = prixBien * 0.01; // 1% entretien
    const chargeTotale = chargeAnnuelle + chargeAmortissement + chargeEntretien;
    const tauxEffort = (chargeTotale / revenuAnnuel) * 100;
    
    // Capacité d'emprunt théorique (5% taux calculatoire)
    const chargeCalculatoire = montantHypotheque * 0.05;
    const chargeCalculatoireTotale = chargeCalculatoire + chargeAmortissement + chargeEntretien;
    const tauxEffortCalculatoire = (chargeCalculatoireTotale / revenuAnnuel) * 100;
    
    // Déterminer la faisabilité
    let faisabilite = '';
    let couleur = '';
    if (tauxEffortCalculatoire <= 33) {
      faisabilite = 'Excellent ! Vous êtes largement qualifié';
      couleur = 'text-green-400';
    } else if (tauxEffortCalculatoire <= 40) {
      faisabilite = 'Limite ! Négociations possibles';
      couleur = 'text-yellow-400';
    } else {
      faisabilite = 'Difficile avec ce revenu';
      couleur = 'text-red-400';
    }
    
    setResult({
      fondsPropresMontant,
      montantHypotheque,
      chargeMensuelle: chargeTotale / 12,
      tauxEffort,
      tauxEffortCalculatoire,
      faisabilite,
      couleur
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleSendPDF = async () => {
    if (!email || !result) return;
    
    setLoading(true);
    
    // Sauvegarder la position actuelle du scroll
    const scrollPosition = window.scrollY;
    
    try {
      const response = await fetch(`${API_BASE_URL}/lead-magnet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          guide: 'Guide Hypothèque Neuchâtel',
          position: 'simulator',
          simulator_data: {
            prixBien: prixBien,
            fondsPropresPct: fondsPropresPct,
            revenuAnnuel: revenuAnnuel,
            tauxInteret: tauxInteret,
            fondsPropresDispo: result.fondsPropresMontant,
            montantHypotheque: result.montantHypotheque,
            chargeMensuelle: result.chargeMensuelle,
            tauxEffortCalculatoire: result.tauxEffortCalculatoire,
            faisabilite: result.faisabilite
          }
        })
      });
      
      if (response.ok) {
        setSuccess(true);
        
        // Restaurer la position du scroll
        setTimeout(() => {
          window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
        }, 0);
        
        setTimeout(() => setSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Error sending PDF:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8 bg-gradient-to-br from-orange-500/10 to-red-500/10" id="simulateur">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-gradient-to-br from-orange-500 to-red-600 p-3 rounded-xl">
          <Calculator className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
          🏠 Calculez votre capacité d'emprunt hypothécaire
        </h3>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Prix du bien immobilier (CHF)
          </label>
          <input
            type="range"
            min="300000"
            max="2000000"
            step="50000"
            value={prixBien}
            onChange={(e) => setPrixBien(Number(e.target.value))}
            className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
          />
          <div className="text-right text-2xl font-bold text-white mt-2">
            {formatCurrency(prixBien)}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Fonds propres (%)
          </label>
          <input
            type="range"
            min="20"
            max="50"
            step="5"
            value={fondsPropresPct}
            onChange={(e) => setFondsPropresPct(Number(e.target.value))}
            className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
          />
          <div className="text-right text-2xl font-bold text-white mt-2">
            {fondsPropresPct}% = {formatCurrency(prixBien * (fondsPropresPct / 100))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Revenu annuel brut (CHF)
          </label>
          <input
            type="range"
            min="60000"
            max="300000"
            step="10000"
            value={revenuAnnuel}
            onChange={(e) => setRevenuAnnuel(Number(e.target.value))}
            className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
          />
          <div className="text-right text-2xl font-bold text-white mt-2">
            {formatCurrency(revenuAnnuel)}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Taux d'intérêt estimé (%)
          </label>
          <input
            type="range"
            min="1.5"
            max="5.0"
            step="0.1"
            value={tauxInteret}
            onChange={(e) => setTauxInteret(Number(e.target.value))}
            className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
          />
          <div className="text-right text-2xl font-bold text-white mt-2">
            {tauxInteret.toFixed(1)}%
          </div>
        </div>

        <button
          onClick={calculateCapacite}
          className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-4 rounded-xl font-semibold hover:from-orange-600 hover:to-red-700 transition-all transform hover:scale-105 flex items-center justify-center gap-2"
        >
          <Calculator className="w-5 h-5" />
          Calculer ma capacité d'emprunt
        </button>

        {result && (
          <div className="mt-6 p-6 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-xl border border-orange-500/30">
            <div className="flex items-center gap-2 mb-4">
              <Home className="w-6 h-6 text-orange-400" />
              <h4 className="text-xl font-bold text-white">Votre capacité d'emprunt</h4>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Fonds propres nécessaires :</span>
                <span className="text-xl font-bold text-white">{formatCurrency(result.fondsPropresMontant)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Montant hypothèque :</span>
                <span className="text-2xl font-bold text-orange-400">{formatCurrency(result.montantHypotheque)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Charge mensuelle estimée :</span>
                <span className="text-xl font-bold text-white">{formatCurrency(result.chargeMensuelle)}</span>
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-white/10">
                <span className="text-gray-300">Taux d'effort (5% calc.) :</span>
                <span className="text-xl font-bold text-white">{result.tauxEffortCalculatoire.toFixed(1)}%</span>
              </div>
              <div className="mt-4 p-4 bg-blue-500/20 rounded-lg border border-blue-500/30">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-blue-300 mb-1">Faisabilité :</p>
                    <p className={`font-bold text-lg ${result.couleur}`}>{result.faisabilite}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
              <p className="text-sm text-gray-300 mb-3">
                📧 Recevez votre rapport personnalisé par email
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  placeholder="Votre email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
                />
                <button
                  onClick={handleSendPDF}
                  disabled={!email || loading}
                  className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-orange-600 hover:to-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {loading ? 'Envoi...' : 'Recevoir'}
                </button>
              </div>
              {success && (
                <p className="text-green-400 text-sm mt-2">✅ Rapport envoyé avec succès ! Consultez votre boîte mail.</p>
              )}
            </div>
            
            <Link
              to="/contact"
              className="mt-4 w-full bg-white text-orange-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-all flex items-center justify-center gap-2"
            >
              Obtenir une offre hypothécaire personnalisée
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniSimulateurHypotheque;